import { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "@/lib/api";
import { Spinner } from "@/components/common";
import { Search, RefreshCw, Maximize2, Minimize2, Check, HelpCircle, ExternalLink, RotateCw, Trash2, AlertTriangle, Clock } from "lucide-react";
import { timeAgo } from "@/lib/ui";
import { onLive } from "@/lib/live";
import { toast } from "sonner";

const CLASS_LABEL = {
  Critical: "Critical Updates", Security: "Security Updates", Definition: "Definition Updates",
  Feature: "Feature Updates", Driver: "Driver Updates", Rollup: "Update Rollups",
};

const kbNum = (kb) => (kb || "").replace(/\D/g, "");
const fmtDate = (s) => { if (!s) return "—"; const d = new Date(s); return isNaN(d.getTime()) ? s : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };
const fmtSize = (mb) => (mb == null ? "—" : mb >= 1024 ? `${(mb / 1024).toFixed(mb % 1024 ? 1 : 0)} GB` : `${mb} MB`);

function CvssBadge({ v }) {
  if (v == null) return <span className="text-slate-600">—</span>;
  const cls = v >= 9 ? "bg-rose-500/15 text-rose-300" : v >= 7 ? "bg-orange-500/15 text-orange-300" : v >= 4 ? "bg-amber-500/15 text-amber-300" : "bg-slate-700/40 text-slate-300";
  return <span className={"inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold " + cls}>{Number(v).toFixed(1)}</span>;
}

export function PatchManager({ deviceId, hostname, canInstall }) {
  const [patches, setPatches] = useState(null);
  const [tab, setTab] = useState("available");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [queued, setQueued] = useState([]);
  const [updated, setUpdated] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState(null);

  const load = useCallback(() => {
    api.get(`/patches/device/${deviceId}`).then((r) => { setPatches(r.data || []); setUpdated(new Date().toISOString()); }).catch(() => setPatches([]));
  }, [deviceId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { if (tab === "history" && history === null) api.get(`/patches/device/${deviceId}/history`).then((r) => setHistory(r.data || [])).catch(() => setHistory([])); }, [tab, deviceId, history]);

  useEffect(() => {
    const off = onLive((e) => {
      if (e.device_id !== deviceId) return;
      if (e.type === "patch") { load(); setQueued([]); setHistory(null); if (e.state !== "failed") toast.success("Patch action completed on this device"); }
      else if (e.type === "job") { load(); }
    });
    return off;
  }, [deviceId, load]);

  const retry = async (kb) => { try { await api.post("/patches/install", { device_ids: [deviceId], kbs: [kb] }); toast.success(`Retry queued for ${kb}`); setQueued((qd) => [...new Set([...qd, kb])]); } catch (e) { toast.error(apiErr(e)); } };
  const uninstall = async (kb) => { if (!window.confirm(`Uninstall ${kb}? The endpoint may require a reboot.`)) return; try { await api.post("/patches/uninstall", { device_ids: [deviceId], kbs: [kb] }); toast.success(`Uninstall queued for ${kb}`); } catch (e) { toast.error(apiErr(e)); } };

  if (!patches) return <div className="card-surface rounded-lg p-6"><Spinner /></div>;

  const available = patches.filter((p) => !p.installed_state);
  const installed = patches.filter((p) => p.installed_state);
  const rows = (tab === "available" ? available : installed).filter((p) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [p.kb, p.title, p.classification, p.supported_products].some((v) => (v || "").toLowerCase().includes(s));
  });

  const toggle = (kb) => setSel((s) => (s.includes(kb) ? s.filter((x) => x !== kb) : [...s, kb]));
  const allSel = rows.length > 0 && rows.every((p) => sel.includes(p.kb));
  const toggleAll = () => setSel(allSel ? [] : rows.map((p) => p.kb));

  const installSelected = async () => {
    try {
      await api.post("/patches/install", { device_ids: [deviceId], kbs: sel });
      toast.success(`Install of ${sel.length} patch(es) queued`);
      setQueued((qd) => [...new Set([...qd, ...sel])]); setSel([]);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const inner = (
    <div className="card-surface rounded-lg overflow-hidden" data-testid="patch-manager">
      <div className="px-4 pt-4 pb-2 border-b border-slate-800">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading font-bold text-lg text-slate-100">Patch management {hostname ? <span className="text-slate-400">for ‘{hostname}’</span> : null}</h2>
            <p className="text-[12px] text-slate-500 mt-0.5">Manage available and installed operating system &amp; security updates.</p>
          </div>
          <button data-testid="patch-expand" onClick={() => setExpanded((v) => !v)} title={expanded ? "Collapse" : "Expand"} className="text-slate-500 hover:text-slate-200 p-1">
            {expanded ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>
        <div className="flex gap-4 mt-3">
          <button data-testid="patch-tab-available" onClick={() => { setTab("available"); setSel([]); }}
            className={"pb-2 text-[13px] font-semibold border-b-2 -mb-px " + (tab === "available" ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-slate-200")}>Available patches ({available.length})</button>
          <button data-testid="patch-tab-installed" onClick={() => { setTab("installed"); setSel([]); }}
            className={"pb-2 text-[13px] font-semibold border-b-2 -mb-px " + (tab === "installed" ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-slate-200")}>Installed patches ({installed.length})</button>
          <button data-testid="patch-tab-history" onClick={() => { setTab("history"); setSel([]); }}
            className={"pb-2 text-[13px] font-semibold border-b-2 -mb-px flex items-center gap-1 " + (tab === "history" ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-slate-200")}><Clock size={13} /> Activity</button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="relative w-64 max-w-full">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="patch-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search"
            className="w-full bg-slate-900 border border-slate-700 rounded-md pl-8 pr-3 py-1.5 text-[13px] text-slate-100 outline-none focus:border-cyan-700" />
        </div>
        <div className="flex items-center gap-4">
          {tab === "available" && canInstall && sel.length > 0 && (
            <button data-testid="patch-install-selected" onClick={installSelected} className="text-[12px] font-semibold text-cyan-300 bg-cyan-950/50 border border-cyan-800 rounded-md px-3 py-1.5">Install {sel.length} selected</button>
          )}
          <span className="text-[12px] text-slate-500">Last updated: <span className="text-slate-300">{updated ? timeAgo(updated) : "—"}</span></span>
          <button data-testid="patch-refresh" onClick={load} className="flex items-center gap-1 text-[12px] text-slate-300 hover:text-cyan-300"><RefreshCw size={13} /> Refresh</button>
        </div>
      </div>

      <div className={"overflow-x-auto" + (tab === "history" ? " hidden" : "")}>
        <table className="w-full text-left text-[13px]">
          <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/80 border-y border-slate-800">
            {tab === "available" && canInstall && <th className="px-3 py-2 w-8"><input type="checkbox" data-testid="patch-select-all" checked={allSel} onChange={toggleAll} /></th>}
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2">Release date</th>
            <th className="px-3 py-2">Classification</th>
            <th className="px-3 py-2">Supported products</th>
            <th className="px-3 py-2">Size</th>
            <th className="px-3 py-2">CVEs</th>
            <th className="px-3 py-2">CVSS <HelpCircle size={11} className="inline text-slate-600 -mt-0.5" /></th>
            <th className="px-3 py-2">Reboot required</th>
            <th className="px-3 py-2">Status</th>
            {canInstall && <th className="px-3 py-2 text-right">Actions</th>}
          </tr></thead>
          <tbody className="divide-y divide-slate-800/60">
            {rows.length === 0 && <tr><td colSpan={11} className="p-8 text-center text-slate-500">{tab === "available" ? "No available patches. This endpoint is up to date." : "No installed patch history reported."}</td></tr>}
            {rows.map((p) => {
              const isQueued = queued.includes(p.kb);
              return (
                <tr key={p.kb} className="hover-row align-top" data-testid="patch-row">
                  {tab === "available" && canInstall && <td className="px-3 py-3"><input type="checkbox" data-testid={`patch-check-${p.kb}`} checked={sel.includes(p.kb)} onChange={() => toggle(p.kb)} disabled={isQueued} /></td>}
                  <td className="px-3 py-3 text-slate-200 max-w-md">
                    {p.title}{" "}
                    <a href={`https://support.microsoft.com/help/${kbNum(p.kb)}`} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 whitespace-nowrap">({p.kb}) <ExternalLink size={10} className="inline -mt-0.5" /></a>
                  </td>
                  <td className="px-3 py-3 text-slate-400 whitespace-nowrap">{fmtDate(p.release_date)}</td>
                  <td className="px-3 py-3 text-slate-300 whitespace-nowrap">{CLASS_LABEL[p.classification] || p.classification}</td>
                  <td className="px-3 py-3 text-slate-400">{p.supported_products || "—"}</td>
                  <td className="px-3 py-3 text-slate-400 whitespace-nowrap">{fmtSize(p.size_mb)}</td>
                  <td className="px-3 py-3">{p.cves ? <span className="text-cyan-400 font-semibold">{p.cves}</span> : <span className="text-slate-600">—</span>}</td>
                  <td className="px-3 py-3"><CvssBadge v={p.cvss} /></td>
                  <td className="px-3 py-3">{p.reboot_required ? <Check size={15} className="text-slate-300" /> : <span className="text-slate-600">—</span>}</td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {p.installed_state ? <span className="inline-flex flex-col"><span className="inline-flex items-center gap-1.5 text-emerald-300"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Installed</span>{p.installed_at && <span className="text-[10px] text-slate-500 ml-3">{fmtDate(p.installed_at)}</span>}</span>
                      : isQueued ? <span className="inline-flex items-center gap-1.5 text-cyan-300"><span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />Queued</span>
                        : p.install_failed ? <span data-testid="patch-status-failed" className="inline-flex items-center gap-1.5 text-rose-300" title={p.last_error || "Install failed"}><AlertTriangle size={12} />Failed</span>
                          : <span className="inline-flex items-center gap-1.5 text-slate-400"><span className="w-1.5 h-1.5 rounded-full bg-slate-500" />Pending</span>}
                  </td>
                  {canInstall && <td className="px-3 py-3 text-right whitespace-nowrap">
                    {p.installed_state ? <button data-testid={`patch-uninstall-${p.kb}`} onClick={() => uninstall(p.kb)} className="text-[11px] text-rose-300 bg-rose-950/40 border border-rose-900 rounded px-2 py-0.5 inline-flex items-center gap-1"><Trash2 size={11} /> Uninstall</button>
                      : p.install_failed && !isQueued ? <button data-testid={`patch-retry-${p.kb}`} onClick={() => retry(p.kb)} className="text-[11px] text-cyan-300 bg-cyan-950/50 border border-cyan-800 rounded px-2 py-0.5 inline-flex items-center gap-1"><RotateCw size={11} /> Retry</button>
                        : <span className="text-slate-700">—</span>}
                  </td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {tab === "history" && <PatchActivity items={history} />}
    </div>
  );

  if (expanded) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-auto" onClick={() => setExpanded(false)}>
        <div className="w-full max-w-6xl my-4" onClick={(e) => e.stopPropagation()}>{inner}</div>
      </div>
    );
  }
  return inner;
}

const ACT_META = {
  install: { verb: "Installed", cls: "text-emerald-300" },
  uninstall: { verb: "Uninstalled", cls: "text-amber-300" },
};

function PatchActivity({ items }) {
  if (items === null) return <div className="p-6"><Spinner /></div>;
  if (items.length === 0) return <div className="p-8 text-center text-slate-500">No patch activity recorded yet for this device.</div>;
  return (
    <div className="p-4 space-y-2" data-testid="patch-activity">
      {items.map((h) => {
        const failed = h.status !== "SUCCEEDED";
        const m = ACT_META[h.action] || { verb: h.action, cls: "text-slate-300" };
        return (
          <div key={h.id} className="flex items-start gap-3 bg-slate-900/50 rounded-md px-3 py-2 text-[13px]" data-testid="patch-activity-row">
            <span className={"mt-0.5 " + (failed ? "text-rose-400" : m.cls)}>{failed ? <AlertTriangle size={14} /> : <Check size={14} />}</span>
            <div className="min-w-0 flex-1">
              <div className="text-slate-200">
                <span className={failed ? "text-rose-300" : m.cls}>{failed ? `${m.verb} failed` : m.verb}</span>{" "}
                <span className="font-mono text-cyan-300">{h.kb}</span>
                {h.title && <span className="text-slate-500"> — {h.title}</span>}
              </div>
              {failed && h.error && <div className="text-[12px] text-rose-300/80 mt-0.5 truncate" title={h.error}>{h.error}</div>}
              <div className="text-[11px] text-slate-500 mt-0.5">{fmtDate(h.at)} · {h.requested_by || "system"}{h.source ? ` · ${h.source}` : ""}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
